import React, { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HelpCircle, Plus, X } from "lucide-react";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { 
  questionsFormSchema, 
  QuestionsFormValues,
  questionsDefaultValues
} from "../schemas/questionsFormSchema";

interface QuestionsFormProps {
  onSectionComplete: (nextSection: string) => void;
}

export const QuestionsForm = ({ onSectionComplete }: QuestionsFormProps) => {
  const { toast } = useToast();
  
  // Initialize the questions form
  const form = useForm<QuestionsFormValues>({
    resolver: zodResolver(questionsFormSchema),
    defaultValues: questionsDefaultValues,
  });

  // Use field array to handle dynamic question fields
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("proposal-questions");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData) as QuestionsFormValues;
        form.reset(parsedData);
      } catch (error) {
        console.error("Error parsing saved questions data:", error);
      }
    }
  }, [form]);

  // Form submission handler
  function onSubmit(data: QuestionsFormValues) {
    console.log("Questions form submitted:", data);
    // Save the form data to local storage or state management
    localStorage.setItem("proposal-questions", JSON.stringify(data));
    
    // Show success toast
    toast({
      title: "Questions saved",
      description: "Moving to the Reverse Auction section",
    });
    
    // Navigate to the next section
    onSectionComplete("reverse-auction");
  }

  // Function to add a new question field with the explanation field
  const addQuestionField = () => {
    append({ question: "", explanation: "", isRequired: false });
  };

  return (
    <div className="transition-opacity">
      <div className="flex items-center mb-4">
        <HelpCircle className="h-6 w-6 text-black mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">Questions</h2>
      </div>
      <p className="text-gray-600 mb-6">Add questions for clients to answer</p>
      
      <Card className="mt-4">
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div key={field.id} className="p-4 border border-gray-200 rounded-md space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-medium">Question {index + 1}</h3>
                      {fields.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => remove(index)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                          <span className="sr-only">Remove question</span>
                        </Button>
                      )}
                    </div>
                    
                    <FormField
                      control={form.control}
                      name={`questions.${index}.question`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Question</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter your question here" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name={`questions.${index}.explanation`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Explanation</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Provide additional context or explanation for this question" 
                              className="min-h-[80px]"
                              {...field} 
                            />
                          </FormControl>
                          <FormDescription>
                            This explanation helps clients understand what you're asking for
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name={`questions.${index}.isRequired`}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Required question</FormLabel>
                            <FormDescription>
                              Client must answer this question before submitting
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                ))}
              </div>
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addQuestionField}
                className="flex items-center gap-1"
              >
                <Plus className="h-4 w-4" />
                Add Another Question
              </Button>
              
              <div className="flex justify-end">
                <Button type="submit">Save & Continue</Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
